import baseApi from './RootBaseApi';

export interface GeoJsonFileMeta {
  id: number;
  geojson_filename: string;
  geojson_storage_path: string;
  excel_filename?: string | null;
  excel_storage_path?: string | null;
  created_at?: string;
  description?: string;
  notes?: string | null;
  height?: string | null;
  depth?: string | null;
  height_m?: number | null;
  depth_m?: number | null;
}

export interface VersionFileMeta {
  id: number;
  version: number;
  merged_filename: string;
  is_current: boolean;
  features_count: number;
  created_at: string;
  notes?: string | null;
  name?: string;
  version_name?: string;
}

export interface VersionFilesResponse {
  total_items: number;
  page: number;
  total_pages: number;
  page_size: number;
  versions: VersionFileMeta[];
}

export interface VersionDetailsResponse {
  id: number;
  version: number;
  merged_filename: string;
  is_current: boolean;
  features_count: number;
  created_at: string;
  geojson: GeoJSON.FeatureCollection;
  name?: string;
}

export interface SourceDetailsResponse {
  version_id: number;
  version: number;
  version_filename: string;
  source_files: Array<{
    id: number;
    geojson_filename: string;
    excel_filename: string;
    has_geojson_content: boolean;
    features_count: number;
    created_at: string;
  }>;
  source_file_count: number;
}

export interface CreateVersionRequest {
  version_id?: string;
  source_file_ids?: string[];
  version_name?: string;
  release_note?: string;
  name?: string;
}

export interface GeoJsonFilesResponse {
  total_items: number;
  page: number;
  total_pages: number;
  page_size: number;
  files: GeoJsonFileMeta[];
}

export interface ManualAnnotationFileMeta {
  id: number;
  name?: string;
  description?: string;
  notes?: string | null;
  height_m?: number | null;
  depth_m?: number | null;
  height?: string | null;
  depth?: string | null;
  geojson_filename: string;
  geojson_storage_path: string;
  created_at?: string;
  updated_at?: string | null;
}

export interface ManualAnnotationsResponse {
  total_items: number;
  total_pages: number;
  page: number;
  page_size: number;
  files: ManualAnnotationFileMeta[];
}

export interface ManualAnnotationDetailsResponse {
  id: number;
  name?: string;
  description?: string;
  notes?: string | null;
  tags?: string[];
  height?: string | null;
  depth?: string | null;
  height_m?: number | null;
  depth_m?: number | null;
  geojson_filename: string;
  geojson_download_url: string;
  created_at: string;
  updated_at?: string | null;
  source?: string;
  // When requesting with geojson=true, backend returns inline geojson content
  geojson_content?: GeoJSON.FeatureCollection;
}

export interface ManualAnnotationUpdatePayload {
  name: string;
  description?: string;
  tags?: string[];
}

export interface ApiResponse {
  success: boolean;
  message: string;
}

export interface RoutingGraphDiagnosticsPayload {
  origin: [number, number];
  destination: [number, number];
  min_depth: number;
  min_height: number;
  allow_unknown_height: true;
  include_reachability_geometry: true;
}

export interface RoutingGraphDiagnosticsResponse {
  connected: boolean;
  status: string;
  failure_reason?: string | null;
  snapped?: Record<string, unknown>;
  reachability?: Record<string, unknown>;
  gap?: Record<string, unknown>;
  classification?: Record<string, unknown>;
  segments?: string[];
  junction_analysis?: Record<string, unknown>;
  route_geojson?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface GeoJsonFileContentResponse {
  features: Array<GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>>;
}

export interface GeoJsonFileWithContent extends GeoJsonFileMeta {
  geojson_content: GeoJsonFileContentResponse;
}
export interface AidsNavigationFileWithContent extends GeoJsonFileMeta {
  geojson: GeoJsonFileContentResponse;
}

export const RoutingManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    downloadGeoJsonFile: builder.query<Blob, number>({
      query: (fileId: number) => ({
        url: `geojson/download/${fileId}`,
        method: 'GET',
        responseType: 'blob',
      }),
      // No transform response as we want the raw blob
      providesTags: ['ExternalServices'],
      // Adding options to make it suitable for file download
      keepUnusedDataFor: 0,
    }),
    getGeoJsonFiles: builder.query<
      GeoJsonFilesResponse,
      { page: number; page_size: number }
    >({
      query: ({ page, page_size }) => ({
        url: `geojson/list?page=${page}&page_size=${page_size}`,
        method: 'GET',
      }),
      transformResponse: (response: GeoJsonFilesResponse) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    getManualAnnotations: builder.query<
      ManualAnnotationsResponse,
      { page: number; page_size: number; search?: string }
    >({
      query: ({ page, page_size, search }) => ({
        url: `manual_annotation/list?page=${page}&page_size=${page_size}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: ManualAnnotationsResponse) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    getManualAnnotationDetails: builder.query<
      ManualAnnotationDetailsResponse,
      { id: number; geojson?: boolean }
    >({
      query: ({ id, geojson }) => ({
        url: `manual_annotation/get/${id}`,
        method: 'GET',
        headers: { Accept: 'application/json' },
        params: geojson ? { geojson: true } : undefined,
      }),
      transformResponse: (response: ManualAnnotationDetailsResponse) =>
        response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    getAllGeoJson: builder.query<GeoJSON.FeatureCollection, void>({
      query: () => ({
        url: 'geojson/all',
        method: 'GET',
        params: { allow_main_content: false, category: 'VL5s' },
        headers: { Accept: 'application/json' },
      }),
      // Backend wraps the FeatureCollection in { files_included, features_count, geojson }
      // We only need the inner geojson FeatureCollection here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) =>
        response.geojson as GeoJSON.FeatureCollection,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    getGeoJsonFileContent: builder.query<GeoJsonFileWithContent, number>({
      query: (fileId: number) => ({
        url: `geojson/get/${fileId}/content`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      transformResponse: (response: GeoJsonFileWithContent) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getAidsToNavigationFileContent: builder.query<any, number>({
      query: (fileId: number) => ({
        url: `/aids-to-navigation/by-file-id/${fileId}`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getFairwayFileContent: builder.query<any, number>({
      query: (fileId: number) => ({
        url: `/fairway-areas/by-file-id/${fileId}`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    runRoutingGraphDiagnostics: builder.mutation<
      RoutingGraphDiagnosticsResponse,
      RoutingGraphDiagnosticsPayload
    >({
      query: (data) => ({
        url: '/manual_publish/graph-diagnostics',
        method: 'POST',
        data,
      }),
    }),

    uploadGeoJsonFile: builder.mutation<ApiResponse, FormData>({
      query: (formData) => ({
        url: 'geojson/add',
        method: 'POST',
        data: formData,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    uploadExcelFile: builder.mutation<ApiResponse, FormData>({
      query: (formData) => ({
        url: 'geojson/upload',
        method: 'POST',
        data: formData,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    deleteGeoJsonFile: builder.mutation<ApiResponse, number>({
      query: (fileId: number) => ({
        url: `geojson/delete/${fileId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    deleteManualAnnotation: builder.mutation<ApiResponse, number>({
      query: (fileId: number) => ({
        url: `manual_annotation/delete/${fileId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    getVersionFiles: builder.query<
      VersionFilesResponse,
      { page: number; page_size: number }
    >({
      query: ({ page, page_size }) => ({
        url: `publish/versions/list?page=${page}&page_size=${page_size}`,
        method: 'GET',
      }),
      transformResponse: (response: VersionFilesResponse) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    setCurrentVersion: builder.mutation<ApiResponse, number>({
      query: (versionId: number) => ({
        url: `publish/version/${versionId}/set-current`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    getVersionDetails: builder.query<VersionDetailsResponse, number>({
      query: (versionId: number) => ({
        url: `publish/versions/${versionId}`,
        method: 'GET',
      }),
      transformResponse: (response: VersionDetailsResponse) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    getSourceDetails: builder.query<SourceDetailsResponse, number>({
      query: (versionId: number) => ({
        url: `publish/versions/${versionId}/source-files`,
        method: 'GET',
      }),
      transformResponse: (response: SourceDetailsResponse) => response,
      providesTags: ['ExternalServices'],
      keepUnusedDataFor: 0,
    }),
    createNewVersion: builder.mutation<ApiResponse, CreateVersionRequest>({
      query: (payload) => ({
        url: `publish/new`,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    createVersionWithVersionID: builder.mutation<
      ApiResponse,
      CreateVersionRequest
    >({
      query: (payload) => ({
        url: `publish/`,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    manualAnnotationAdd: builder.mutation<ApiResponse, FormData>({
      query: (formData) => ({
        url: 'manual_annotation/add',
        method: 'POST',
        data: formData,
        headers: { Accept: 'application/json' },
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    manualAnnotationUpdate: builder.mutation<
      ApiResponse,
      { id: number; payload: ManualAnnotationUpdatePayload }
    >({
      query: ({ id, payload }) => ({
        url: `manual_annotation/${id}`,
        method: 'PUT',
        data: payload,
        headers: { Accept: 'application/json' },
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    manualAnnotationEditDimensions: builder.mutation<
      ApiResponse,
      { id: number; payload: FormData }
    >({
      query: ({ id, payload }) => ({
        url: `manual_annotation/edit/${id}`,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    manualPublishUpdate: builder.mutation<ApiResponse, void>({
      query: () => ({
        url: 'manual_publish/update',
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
    confirmPublish: builder.mutation<ApiResponse, void>({
      query: () => ({
        url: 'publish/confirm',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse) => response,
    }),
    updateGeoJsonFeature: builder.mutation<
      ApiResponse,
      { fileId: number; featureIndex: number; payload: FormData }
    >({
      query: ({ fileId, featureIndex, payload }) => ({
        url: `geojson/${fileId}/feature/${featureIndex}`,
        method: 'PUT',
        data: payload,
      }),
      transformResponse: (response: ApiResponse) => response,
      invalidatesTags: ['ExternalServices'],
    }),
  }),
});

export const {
  useDownloadGeoJsonFileQuery,
  useLazyDownloadGeoJsonFileQuery,
  useGetGeoJsonFilesQuery,
  useGetManualAnnotationsQuery,
  useGetManualAnnotationDetailsQuery,
  useGetAllGeoJsonQuery,
  useGetGeoJsonFileContentQuery,
  useGetAidsToNavigationFileContentQuery,
  useGetFairwayFileContentQuery,
  useRunRoutingGraphDiagnosticsMutation,
  useUploadGeoJsonFileMutation,
  useUploadExcelFileMutation,
  useDeleteGeoJsonFileMutation,
  useDeleteManualAnnotationMutation,
  useGetVersionFilesQuery,
  useSetCurrentVersionMutation,
  useGetVersionDetailsQuery,
  useGetSourceDetailsQuery,
  useCreateNewVersionMutation,
  useCreateVersionWithVersionIDMutation,
  useManualAnnotationAddMutation,
  useManualAnnotationUpdateMutation,
  useManualAnnotationEditDimensionsMutation,
  useManualPublishUpdateMutation,
  useConfirmPublishMutation,
  useUpdateGeoJsonFeatureMutation,
} = RoutingManagementService;
